import classNames from 'classnames';
import * as React from 'react';
import { FiSearch } from 'react-icons/fi';
import { search } from '../../state/search';
import { Task } from '../../types';
import { getTaskCategoryColor, zeroPadId } from '../../utils/taskUtils';

type SearchInputProps = {
  className?: string;
  onSelect: (task: Task) => void;
  onClear: () => void;
};

const SearchInput = (props: SearchInputProps) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchResults, setSearchResults] = React.useState<Task[]>([]);
  const [activeResultIndex, setActiveResultIndex] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const itemsRef = React.useRef<HTMLDivElement[]>([]);

  React.useEffect(() => {
    const results = search(searchTerm).map((result) => result.item);
    setActiveResultIndex(0);
    setSearchResults(results);
  }, [searchTerm]);

  const handleSelect = () => {
    const task = searchResults[activeResultIndex];
    if (!task) {
      return
    }

    props.onSelect(task);
    setSearchTerm(`${task.id}`);
    inputRef.current?.blur();
  };

  React.useEffect(() => {
    itemsRef.current = itemsRef.current.slice(0, searchResults.length);
  }, [searchResults]);

  React.useEffect(() => {
    if (itemsRef.current[activeResultIndex]) {
      itemsRef.current[activeResultIndex].scrollIntoView({
        block: 'nearest',
      });
    }
  }, [activeResultIndex]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveResultIndex(Math.min(activeResultIndex + 1, searchResults.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveResultIndex(Math.max(activeResultIndex - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      handleSelect();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      props.onClear();
      setIsFocused(false);
      setSearchResults([]);
      setSearchTerm('');
    }
  };

  const toggleFocus = (event: React.FocusEvent) => {
    setIsFocused(event.type === 'focus');
  };

  const handleItemMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    handleSelect();
  };

  return (
    <div
      className={classNames('relative', props.className)}
    >
      <input
        className="h-8 w-full pr-8 bg-light px-2 text-sm outline-none border border-transparent focus:border-gray-800"
        type="text"
        ref={inputRef}
        placeholder="Søg"
        value={searchTerm}
        onFocus={toggleFocus}
        onBlur={toggleFocus}
        onKeyDown={handleKeyDown}
        onChange={(event) => {
          setSearchTerm(event.target.value);
        }}
      />
      <div className="w-8 h-8 absolute top-0 right-0 flex items-center justify-center -mt-px">
        <FiSearch />
      </div>

      {isFocused && searchTerm && (
        <div
          className="absolute top-full w-full bg-white z-10 shadow-xl divide-y border max-h-60 overflow-y-auto"
        >
          {searchResults.length === 0 && (
            <div className="text-sm text-center px-2 py-4">Ingen resultater</div>
          )}
          {searchResults.map((task, index) => (
            <div
              key={task.id}
              className={classNames(
                'text-xs px-2 py-1.5 flex items-start cursor-pointer',
                { 'bg-gray-100': activeResultIndex === index },
              )}
              onMouseDown={handleItemMouseDown}
              onMouseOver={() => setActiveResultIndex(index)}
              ref={(element) => {
                itemsRef.current[index] = element!;
              }}
              onClick={handleSelect}
            >
              <div className="flex items-center mt-0.5">
                <span className="font-semibold">{zeroPadId(task.id)}</span>
                <div
                  className={`flex-none w-1.5 h-1.5 ml-1 ${getTaskCategoryColor(task.category)}`}
                />
              </div>
              <div className="ml-2.5 py-0.5">{task.title}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchInput;
